import { getSimpleGrade, toGrade } from './Grade'
import { RasnChannel } from './RASN'

export const RASN_CHANNELS: readonly RasnChannel[] = [
  RasnChannel.DIGITAL,
  RasnChannel.NON_DIGITAL,
  RasnChannel.WHOLESALE,
  RasnChannel.RETAIL
]

// Mirrors GradingInstructionValidator.SHIP_TO_PATTERN on the backend
// (alphanumerics plus - _ /). Drift between the two regexes silently
// rejects values one side accepts.
export const SHIPTO_REGEX = /^[A-Za-z0-9\-_/]{1,64}$/
const COUNTRY_SHAPE_REGEX = /^[A-Z]{2}$/

export interface FilterCondition {
  channels: RasnChannel[]
  shipTos: string[]
  countries: string[]
}

export const newFilterCondition = (): FilterCondition => ({ channels: [], shipTos: [], countries: [] })

export const cloneFilterCondition = (c: FilterCondition): FilterCondition => ({
  channels: [...c.channels],
  shipTos: [...c.shipTos],
  countries: [...c.countries]
})

const isConditionEmpty = (c: FilterCondition): boolean =>
  c.channels.length === 0 && c.shipTos.length === 0 && c.countries.length === 0

// Returns the first validation error for the condition, or null if valid.
// Rules:
//   1. At least one of channels, shipTos, or countries must be populated.
//      (An all-empty condition would be persisted as zero filter rows on the
//      backend and silently disappear on the next read.)
//   2. ShipTo and Country are mutually exclusive within a single condition.
//   3. When `validCountryCodes` is supplied, each country must be a member
//      (matches the backend's ISO-3166-1 alpha-2 check). When omitted, only
//      the two-uppercase-letter shape is checked, so existing call sites
//      keep the previous behaviour.
export const validateCondition = (
  c: FilterCondition,
  validCountryCodes?: ReadonlySet<string>
): string | null => {
  if (isConditionEmpty(c)) {
    return 'Filter condition cannot be empty — set at least one of Channel, Ship To, or Country'
  }
  if (c.channels.some(ch => !RASN_CHANNELS.includes(ch))) {
    return 'Invalid channel value'
  }
  if (c.shipTos.some(s => !SHIPTO_REGEX.test(s))) {
    return 'ShipTo codes must be 1-64 letters, digits, or - _ / (e.g. ABC1067)'
  }
  if (c.countries.some(co => !COUNTRY_SHAPE_REGEX.test(co))) {
    return 'Country must be a 2-letter ISO code (e.g. US, DE)'
  }
  if (validCountryCodes && c.countries.some(co => !validCountryCodes.has(co))) {
    return 'Country must be a recognised ISO-3166-1 country (e.g. US, DE)'
  }
  if (c.shipTos.length > 0 && c.countries.length > 0) {
    return 'ShipTo and Country cannot both be set — choose one'
  }
  return null
}

export class GradingInstructionDTO {
  id: number | null
  productCode: string
  label: string
  value: string
  acceptableGrades: string[]
  modifiedBy: string | null
  lastModified: number | null
  conditions: FilterCondition[]

  constructor (instruction?: GradingInstruction) {
    this.id = instruction?.id ?? null
    this.productCode = instruction?.style ? (instruction?.color ? `${instruction?.style}-${instruction?.color}` : instruction.style) : ''
    this.label = instruction?.description ?? ''
    this.value = instruction?.instruction ?? ''
    this.acceptableGrades = instruction?.acceptableGrades.map(i => toGrade(i)) ?? []
    this.modifiedBy = instruction?.modifiedBy ?? null
    this.lastModified = instruction?.lastModified ?? null
    this.conditions = (instruction?.conditions ?? []).map(cloneFilterCondition)
  }
}

export class GradingInstruction {
  id: number | null
  style: string
  color: string
  description: string
  instruction: string
  acceptableGrades: string[]
  modifiedBy: string | null
  lastModified: number | null
  conditions: FilterCondition[]

  constructor (dto?: GradingInstructionDTO) {
    this.id = dto?.id ?? null
    this.style = dto?.productCode.split('-')[0] ?? ''
    this.color = dto?.productCode.split('-')[1] ?? ''
    this.description = dto?.label ?? ''
    this.instruction = dto?.value ?? ''
    this.acceptableGrades = dto?.acceptableGrades.map(i => getSimpleGrade(i)) ?? []
    this.modifiedBy = dto?.modifiedBy ?? null
    this.lastModified = dto?.lastModified ?? null
    this.conditions = (dto?.conditions ?? []).map(cloneFilterCondition)
  }
}
